/**
 * Contracts of the registration Worker's page endpoints
 * (`registrationworker-api`): the gym's public address, what its public form
 * asks for, whether it asks the health questionnaire, and where a confirmed
 * registration goes in the CRM.
 *
 * camelCase on purpose: this is our own Worker, not the Actuar C# backend. The
 * PascalCase rule applies to the `*ViewModel`s.
 */

export type TRegistrationFieldRule = 'off' | 'optional' | 'required';

/**
 * The fields a gym may switch on and off. Name, phone and CPF are not here:
 * they are always asked. CPF in particular is the public form's front door and
 * the key the counter uses to find the candidate.
 */
export interface IRegistrationFormConfig {
  birthdate: TRegistrationFieldRule;
  gender: TRegistrationFieldRule;
  email: TRegistrationFieldRule;
  zipCode: TRegistrationFieldRule;
  /** Street, number, district, city and state travel as one group. */
  address: TRegistrationFieldRule;
  emergencyContact: TRegistrationFieldRule;
}

export type TRegistrationFormField = keyof IRegistrationFormConfig;

/** The two ready-made models of the design; anything else is a custom form. */
export type TRegistrationFormPreset = 'preRegistration' | 'full';

export const REGISTRATION_FORM_PRESETS: Record<TRegistrationFormPreset, IRegistrationFormConfig> = {
  preRegistration: {
    birthdate: 'off',
    gender: 'off',
    email: 'optional',
    zipCode: 'off',
    address: 'off',
    emergencyContact: 'off',
  },
  full: {
    birthdate: 'required',
    gender: 'optional',
    email: 'required',
    zipCode: 'required',
    address: 'required',
    emergencyContact: 'required',
  },
};

/**
 * Where the person lands in the CRM once staff confirm the registration at the
 * counter. All three ids are required by the CRM when `enabled` is true: the
 * funnel entry is refused without a responsible consultant.
 */
export interface IRegistrationCrmConfig {
  enabled: boolean;
  funnelId: string | null;
  stepId: string | null;
  consultantId: string | null;
}

export const REGISTRATION_CRM_DISABLED: IRegistrationCrmConfig = {
  enabled: false,
  funnelId: null,
  stepId: null,
  consultantId: null,
};

/**
 * Whether the public form asks the health questionnaire (PAR-Q): the seven
 * yes/no questions of the physical-assessment anamnesis.
 *
 * A setting of its own, outside `IRegistrationFormConfig`: picking a form
 * preset never switches it on or off. There is no `optional`, because a
 * questionnaire the visitor may skip says nothing about who skipped it.
 */
export interface IRegistrationHealthConfig {
  parq: 'off' | 'required';
}

export const REGISTRATION_HEALTH_OFF: IRegistrationHealthConfig = { parq: 'off' };

export interface IRegistrationPage {
  id: string;
  slug: string;
  displayName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * The Worker sends the three. Without `crm`, the store's `crm` computed
   * answers CRM off.
   */
  form?: IRegistrationFormConfig;
  crm?: IRegistrationCrmConfig;
  health?: IRegistrationHealthConfig;
}

/** Answer of `GET /api/slug/check`, tied to the slug it was asked about. */
export interface IRegistrationSlugCheck {
  slug: string;
  available: boolean;
}
